import type { EditableBoat, LostRow, RelationRow } from './boat-changes';

const FIELD_LABELS: Record<string, string> = {
  name: 'Name', manufacturer: 'Manufacturer', model: 'Model', model_year: 'Year', condition: 'Condition',
  hin_number: 'HIN', length_ft: 'Length', beam_in: 'Beam', draft_in: 'Draft', seating_capacity: 'Seating capacity',
  exterior_color: 'Exterior color', interior_color: 'Interior color', our_price: 'Our price', msrp: 'MSRP',
  regular_price: 'Regular price', sale_price: 'Sale price', cost_price: 'Cost price', is_on_sale: 'On sale',
  featured: 'Featured', invoice_amount: 'Invoice amount', rigging: 'Rigging', freight: 'Freight',
  total_cost: 'Total cost', boat_cost_price: 'Boat cost', boat_retail_price: 'Boat retail',
  description: 'Description', short_description: 'Short description', status_id: 'Status', ribbon_id: 'Ribbon',
  ribbon_expiry_type: 'Ribbon expiry', ribbon_expiry_value: 'Ribbon expiry', first_available_date: 'First available',
  website_status: 'Website', video_url: 'Walk-around video',
};

const ROW_KEY_LABELS: Record<string, string> = {
  engine_manufacturer: 'make', engine_model: 'model', engine_horsepower: 'horsepower', engine_year: 'year',
  engine_hours: 'hours', engine_serial_number: 'serial number', drive_type: 'drive type', fuel_type: 'fuel type',
  trailer_vin: 'VIN', trailer_make: 'make', trailer_model: 'model', trailer_axles: 'axles',
  trailer_gvwr: 'GVWR', shipping_weight: 'shipping weight',
  item: 'name', description: 'description', price: 'price', cost: 'cost', sku: 'SKU', list: 'list',
};

const SECTION_LABELS: Record<string, string> = { engines: 'Engine', trailers: 'Trailer', equipment: 'Equipment' };
const GROUP_LABELS: Record<string, string> = { locations: 'Locations', comparables: 'Comparables' };

function humanize(key: string): string {
  const words = key.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const LIST_LABELS: Record<string, string> = { standard_equipment: 'Standard equipment', optional_equipment: 'Optional equipment' };

function rowName(section: string, row: RelationRow): string {
  const values = row.values;
  if (section === 'engines') return [values.engine_manufacturer, values.engine_model].filter(Boolean).join(' ');
  if (section === 'trailers') return String(values.trailer_vin ?? '');
  return String(values.item ?? '');
}

/**
 * 4E4 review F3 — "Engine 2 (Monsoon M5Di)", "Optional equipment 1 (Radar)": the
 * row's position and name in `editable`, so two rows conflicting on the same key
 * never read the same. Null when the row is not there.
 */
function describeRow(section: string, id: string, editable: EditableBoat): string | null {
  const lists: Array<[string, RelationRow[]]> = section === 'equipment'
    ? [['standard_equipment', editable.standard_equipment], ['optional_equipment', editable.optional_equipment]]
    : section === 'engines' || section === 'trailers' ? [[section, editable[section]]] : [];
  for (const [list, rows] of lists) {
    const index = rows.findIndex((row) => row.id === id);
    if (index === -1) continue;
    const name = rowName(section, rows[index]);
    const label = LIST_LABELS[list] ?? SECTION_LABELS[list] ?? humanize(list);
    return `${label} ${index + 1}${name ? ` (${name})` : ''}`;
  }
  return null;
}

/**
 * A conflict path as the dealer reads it: "Model", "Engine hours", "Locations".
 * Given the state the conflict is in, a row is named: "Engine 2 (Monsoon M5Di) hours".
 */
export function describeChangePath(path: string, editable?: EditableBoat): string {
  if (path.startsWith('group:')) {
    const name = path.slice('group:'.length);
    return GROUP_LABELS[name] ?? humanize(name);
  }
  const [section, id, key] = path.split('.');
  if (key === undefined) return FIELD_LABELS[path] ?? humanize(path);
  const row = editable ? describeRow(section, id, editable) : null;
  return `${row ?? SECTION_LABELS[section] ?? humanize(section)} ${ROW_KEY_LABELS[key] ?? key.replace(/_/g, ' ')}`;
}

export function formatChangeValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '(empty)';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return '(a different selection)';
  return String(value);
}

/** "Engine Monsoon M5Di", "Equipment Depth Finder" — for the "removed elsewhere" warning. */
export function describeLostRow(row: LostRow): string {
  if (row.section === 'engines') {
    return `Engine ${[row.values.engine_manufacturer, row.values.engine_model].filter(Boolean).join(' ')}`.trim();
  }
  if (row.section === 'trailers') return `Trailer ${row.values.trailer_vin ?? ''}`.trim();
  return `Equipment ${row.values.item ?? ''}`.trim();
}
